import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "input",
    "dropZone",
    "currentImage",
    "previewImage",
    "overlay",
    "progress",
    "progressBar",
    "error",
    "actionsWithImage",
    "actionsEmpty",
    "actionsUploading",
    "progressPercent",
    "removeField",
    "emptyState",
  ];
  static values = {
    maxSize: { type: Number, default: 10485760 },
    accept: {
      type: String,
      default: "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp",
    },
    errorFileSize: { type: String, default: "" },
    errorContentType: { type: String, default: "" },
    uploadingText: { type: String, default: "Uploading..." },
  };

  connect() {
    this.form = this.element.closest("form");
    if (!this.form) return;

    this._onDirectUploadStart = this._handleDirectUploadStart.bind(this);
    this._onDirectUploadProgress = this._handleDirectUploadProgress.bind(this);
    this._onDirectUploadError = this._handleDirectUploadError.bind(this);
    this._onDirectUploadEnd = this._handleDirectUploadEnd.bind(this);

    this.form.addEventListener(
      "direct-upload:start",
      this._onDirectUploadStart,
    );
    this.form.addEventListener(
      "direct-upload:progress",
      this._onDirectUploadProgress,
    );
    this.form.addEventListener(
      "direct-upload:error",
      this._onDirectUploadError,
    );
    this.form.addEventListener("direct-upload:end", this._onDirectUploadEnd);
  }

  disconnect() {
    if (!this.form) return;

    this.form.removeEventListener(
      "direct-upload:start",
      this._onDirectUploadStart,
    );
    this.form.removeEventListener(
      "direct-upload:progress",
      this._onDirectUploadProgress,
    );
    this.form.removeEventListener(
      "direct-upload:error",
      this._onDirectUploadError,
    );
    this.form.removeEventListener("direct-upload:end", this._onDirectUploadEnd);
  }

  openFilePicker(event) {
    event.preventDefault();
    this.inputTarget.click();
  }

  dragover(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.add("border-brand-500", "bg-brand-50/50");
    this.dropZoneTarget.classList.remove("border-surface-border");
  }

  dragleave(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.remove("border-brand-500", "bg-brand-50/50");
    this.dropZoneTarget.classList.add("border-surface-border");
  }

  drop(event) {
    event.preventDefault();
    this.dragleave(event);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      this.inputTarget.files = files;
      this.handleFile();
    }
  }

  handleFile() {
    this.clearError();
    const file = this.inputTarget.files[0];
    if (!file) return;

    if (!this.validateFile(file)) return;

    this.showPreview(file);
  }

  validateFile(file) {
    if (this.maxSizeValue > 0 && file.size > this.maxSizeValue) {
      this.showError(this.errorFileSizeValue);
      this.inputTarget.value = "";
      return false;
    }

    const accepted = this.acceptValue.split(",").map((t) => t.trim());
    const acceptedMimes = accepted.filter((t) => t.includes("/"));
    const acceptedExts = accepted.filter((t) => t.startsWith("."));
    const ext = "." + file.name.split(".").pop().toLowerCase();
    if (!acceptedMimes.includes(file.type) && !acceptedExts.includes(ext)) {
      this.showError(this.errorContentTypeValue);
      this.inputTarget.value = "";
      return false;
    }

    return true;
  }

  showPreview(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewImageTarget.innerHTML = `
        <img src="${e.target.result}" alt="Preview" class="w-full h-full object-cover">
      `;
      this.previewImageTarget.classList.remove("hidden");
      this.currentImageTarget.classList.add("hidden");
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.add("hidden");
      }

      this._showActionsWithImage();
    };
    reader.readAsDataURL(file);
  }

  remove(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.inputTarget.value = "";

    if (this.hasRemoveFieldTarget) {
      this.removeFieldTarget.value = "1";
    }

    this.clearPreview();
    this.clearError();
  }

  clearPreview() {
    const hasCurrentImage = this.element.dataset.hasCurrentImage === "true";

    this.previewImageTarget.classList.add("hidden");
    this.previewImageTarget.innerHTML = "";

    if (hasCurrentImage) {
      this.currentImageTarget.classList.remove("hidden");
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.add("hidden");
      }
    } else {
      this.currentImageTarget.classList.add("hidden");
      if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.remove("hidden");
      }
    }

    this._showActionsEmpty();
  }

  _showActionsEmpty() {
    if (this.hasActionsWithImageTarget) {
      this.actionsWithImageTarget.classList.add("hidden");
    }
    if (this.hasActionsUploadingTarget) {
      this.actionsUploadingTarget.classList.add("hidden");
    }
    if (this.hasActionsEmptyTarget) {
      this.actionsEmptyTarget.classList.remove("hidden");
    }
  }

  _showActionsWithImage() {
    if (this.hasActionsEmptyTarget) {
      this.actionsEmptyTarget.classList.add("hidden");
    }
    if (this.hasActionsUploadingTarget) {
      this.actionsUploadingTarget.classList.add("hidden");
    }
    if (this.hasActionsWithImageTarget) {
      this.actionsWithImageTarget.classList.remove("hidden");
    }
  }

  _showActionsUploading() {
    if (this.hasActionsEmptyTarget) {
      this.actionsEmptyTarget.classList.add("hidden");
    }
    if (this.hasActionsWithImageTarget) {
      this.actionsWithImageTarget.classList.add("hidden");
    }
    if (this.hasActionsUploadingTarget) {
      this.actionsUploadingTarget.classList.remove("hidden");
    }
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  clearError() {
    this.errorTarget.textContent = "";
    this.errorTarget.classList.add("hidden");
  }

  // Direct Upload event handlers

  _handleDirectUploadStart() {
    this.progressTarget.classList.remove("hidden");
    this._showActionsUploading();
    this._setSubmitButtonsDisabled(true);
  }

  _handleDirectUploadProgress(event) {
    const { progress } = event.detail;
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`;
    }
    if (this.hasProgressPercentTarget) {
      this.progressPercentTarget.textContent = `${Math.round(progress)}%`;
    }
  }

  _handleDirectUploadError(event) {
    event.preventDefault();
    this.progressTarget.classList.add("hidden");
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "0%";
    }
    if (this.hasProgressPercentTarget) {
      this.progressPercentTarget.textContent = "0%";
    }
    this.showError(event.detail.error);
    this._showActionsEmpty();
    this._setSubmitButtonsDisabled(false);
  }

  _handleDirectUploadEnd() {
    this.progressTarget.classList.add("hidden");
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "0%";
    }
    if (this.hasProgressPercentTarget) {
      this.progressPercentTarget.textContent = "0%";
    }
    this._showActionsWithImage();
    this._setSubmitButtonsDisabled(false);
  }

  _setSubmitButtonsDisabled(disabled) {
    if (!this.form) return;
    this.form.querySelectorAll('[type="submit"]').forEach((btn) => {
      btn.disabled = disabled;
    });
  }
}
